import React, { ButtonHTMLAttributes, HTMLAttributes, PropsWithChildren } from 'react';
import infoIcon from '@/assets/icons/info2.svg';
import styles from './Tile.module.scss';

export interface TileProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  className?: string;
  labelClassName?: string;
  variant?: 'default' | 'add';
}

const Tile = ({ children, selected = false, className = '', labelClassName = '', variant = 'default', ...props }: PropsWithChildren<TileProps>) => {
  const variantClass = variant === 'add' ? styles.addTile : '';
  const classes = `${styles.tile} ${selected ? styles.tileSelected : ''} ${variantClass} ${className}`.trim();
  return (
    <button type="button" className={classes} {...props}>
      {children}
    </button>
  );
};

export interface TileLabelProps extends HTMLAttributes<HTMLSpanElement> {
  className?: string;
  variant?: 'default' | 'add';
}

export const TileLabel = ({ className = '', variant = 'default', ...props }: TileLabelProps) => {
  const variantClass = variant === 'add' ? styles.addTileLabel : '';
  return <span className={`${styles.tileLabel} ${variantClass} ${className}`.trim()} {...props} />;
};

export interface TileIconProps extends HTMLAttributes<HTMLImageElement> {
  className?: string;
  src: string;
  alt?: string;
}

export const TileIcon = ({ className = '', src, alt = '', ...props }: TileIconProps) => {
  return <img className={`${styles.tileIcon} ${className}`.trim()} src={src} alt={alt} {...props} />;
};

export interface TilesGridProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const TilesGrid = ({ className = '', children, ...props }: PropsWithChildren<TilesGridProps>) => {
  return (
    <div className={`${styles.tilesGrid} ${className}`.trim()} {...props}>
      {children}
    </div>
  );
};

export interface TileSubLabelProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  text: string;
  withInfoIcon?: boolean;
}

export const TileSubLabel = ({ className = '', text, withInfoIcon = false, ...props }: TileSubLabelProps) => {
  return (
    <div className={`${styles.tileSubLabel} ${className}`.trim()} {...props}>
      <span className={styles.tileSubLabelText}>{text}</span>
      {withInfoIcon && <img className={styles.tileInfoIcon} src={infoIcon} alt="info" />}
    </div>
  );
};

export interface TileLabelsProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const TileLabels = ({ className = '', children, ...props }: PropsWithChildren<TileLabelsProps>) => {
  return (
    <div className={`${styles.tileLabels} ${className}`.trim()} {...props}>
      {children}
    </div>
  );
};

export default Tile;

export type TileListItem = {
  id: number;
  label: string;
  icon?: string;
  subLabel?: string;
  onClick?: () => void;
};

export interface TileListProps extends TilesGridProps {
  tiles: TileListItem[];
  renderTile?: (tile: TileListItem) => React.ReactNode;
  additionalTiles?: React.ReactNode;
  /** Rendered before the tiles, for an entry point that must not be lost below the fold. */
  leadingTiles?: React.ReactNode;
  tileClassName?: string;
  selectedId?: number;
}

export const TileList = ({ tiles, renderTile, className = '', additionalTiles, leadingTiles, tileClassName = '', selectedId, ...props }: TileListProps) => {
  return (
    <TilesGrid className={className} {...props}>
      {leadingTiles}
      {tiles.map((tile) => (
        renderTile ? (
          <React.Fragment key={tile.id}>{renderTile(tile)}</React.Fragment>
        ) : (
          <Tile key={tile.id} onClick={tile.onClick} className={tileClassName} selected={selectedId === tile.id}>
            {tile.icon && <TileIcon src={tile.icon} alt={tile.label} />}
            {tile.subLabel ? (
              <TileLabels>
                <TileLabel>{tile.label}</TileLabel>
                <TileSubLabel text={tile.subLabel} withInfoIcon/>
              </TileLabels>
            ) : (
              <TileLabel>{tile.label}</TileLabel>
            )}
          </Tile>
        )
      ))}
      {additionalTiles}
    </TilesGrid>
  );
};


